// Phase 2 Task 13 / H-2 — structured error composition for operator-facing toast/inline text.
//
// DGUV Information 215-450, Chapter 8 (Text design):
//   "Avoid accusatory language in error messages. Give clear indications of incorrect
//    fields and use understandable descriptions."
//
// Generic "Fehler beim X" strings tell the user nothing actionable. The structured
// {action} + {cause} + {remedy} template forces the writer to think about WHY the
// failure happened and WHAT the user should do next. mapBackendError (in api.ts)
// translates BE error codes into German cause/remedy pairs.

export interface AppError {
  action: string;    // What the user was trying to do, e.g., "Genehmigen", "Speichern"
  cause: string;     // Why it failed in plain German, e.g., "Server nicht erreichbar"
  remedy?: string;   // Optional next step, e.g., "Bitte später erneut versuchen"
}

export function formatError(e: AppError): string {
  return e.remedy
    ? `${e.action} fehlgeschlagen: ${e.cause}. ${e.remedy}.`
    : `${e.action} fehlgeschlagen: ${e.cause}.`;
}

// Map an axios-style error into a structured German AppError. BE returns text/plain
// error bodies (see smarttix.core AuthHelpers.WriteErrorAsync), so we switch on the
// HTTP status code; BE message text is appended as fallback when status alone isn't
// informative (e.g., 400 BadRequest could be many things).
//
// Lives in utils (not services/api) so test files that auto-mock api.ts via
// `jest.mock('../../services/api')` don't accidentally null out this helper.
export function mapBackendError(err: any, action: string): AppError {
  const status: number | undefined = err?.response?.status;
  const bodyMsg: string | undefined = typeof err?.response?.data === 'string'
    ? err.response.data
    : err?.response?.data?.message;
  const isNetwork = err?.code === 'ERR_NETWORK' || err?.message === 'Network Error';
  const isTimeout = err?.code === 'ECONNABORTED' || err?.code === 'ETIMEDOUT';
  const isAbort = err?.name === 'CanceledError' || err?.name === 'AbortError';

  if (isAbort)   return { action, cause: 'Vom Benutzer abgebrochen' };
  if (isNetwork) return { action, cause: 'Server nicht erreichbar', remedy: 'Bitte Verbindung prüfen und erneut versuchen' };
  if (isTimeout) return { action, cause: 'Zeitüberschreitung', remedy: 'Bitte später erneut versuchen' };

  switch (status) {
    case 400: return { action, cause: bodyMsg || 'Ungültige Anfrage', remedy: 'Bitte Eingaben prüfen' };
    case 401: return { action, cause: 'Sitzung abgelaufen',            remedy: 'Bitte neu anmelden' };
    case 403: return { action, cause: 'Keine Berechtigung',            remedy: 'Bitte mit Administrator klären' };
    case 404: return { action, cause: 'Nicht gefunden',                remedy: 'Bitte Liste aktualisieren' };
    case 409: return { action, cause: bodyMsg || 'Konflikt',           remedy: 'Bitte Eingabe anpassen' };
    case 413: return { action, cause: 'Datei zu groß',                 remedy: 'Bitte kleinere Datei wählen' };
    case 423: return { action, cause: bodyMsg || 'Wartungsarbeiten',   remedy: 'Bitte Seite neu laden' };
    case 429: return { action, cause: 'Zu viele Anfragen',             remedy: 'Bitte kurz warten und erneut versuchen' };
    case 500: return { action, cause: 'Serverfehler',                  remedy: 'Bitte später erneut versuchen' };
    case 502:
    case 503:
    case 504: return { action, cause: 'Server nicht erreichbar',       remedy: 'Bitte später erneut versuchen' };
    default:  return { action, cause: bodyMsg || err?.message || 'Unbekannter Fehler' };
  }
}

// Convenience helper: map + format in one call.
export function formatBackendError(err: any, action: string): string {
  return formatError(mapBackendError(err, action));
}
